/**
 * Supplies the authentication session.
 *
 * On mount it attempts a silent restore from the refresh cookie: a returning
 * user with a valid cookie should land on the page they asked for, not on a
 * login form. A rejection is the ordinary "not signed in" case rather than an
 * error worth showing.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.13, ADR 0004
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { LoginRequest, Role, SessionUser } from '@nexus/contracts';

import { setSessionExpiredHandler } from '../../lib/api-client';
import { AuthContext, type AuthContextValue } from '../../lib/auth-context';
import { hasRole } from '../../lib/auth-types';

import * as authApi from './api/auth.api';

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    /* The API client calls this when a refresh fails, which can happen long
       after mount — an expired session must clear the UI, not leave a stale
       name in the header beside a page that no longer loads.

       No `cancelled` guard here, unlike the promise callbacks below: the
       cleanup deregisters this handler, so it cannot still be invoked after
       teardown. A guard would be a branch no test could reach. */
    setSessionExpiredHandler(() => {
      setUser(null);
    });

    void authApi
      .restoreSession()
      .then((restored) => {
        if (!cancelled) setUser(restored);
      })
      .catch(() => {
        /* No valid cookie. The ordinary not-signed-in case. */
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      setSessionExpiredHandler(null);
    };
  }, []);

  const signIn = useCallback(async (credentials: LoginRequest): Promise<void> => {
    setUser(await authApi.login(credentials));
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    await authApi.logout();
    setUser(null);
  }, []);

  const can = useCallback(
    (required: Role): boolean => hasRole(user?.role, required),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, signIn, signOut, can }),
    [user, isLoading, signIn, signOut, can],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
